import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { Box, SxProps, Theme } from '@mui/material';

import Linkify from 'react-linkify';

import ErrorIcon from '@watershed/icons/components/Error';
import PopOutIcon from '@watershed/icons/components/PopOut';
import WarningIcon from '@watershed/icons/components/Warning';
import { temporalWorkflowUrl } from '@watershed/shared-universal/adminRoutes';
import { makeSerializableError } from '@watershed/shared-universal/utils/serializableError';
import mixinSx from '@watershed/style/mixinSx';
import Button from '@watershed/ui-core/components/Button';
import { Dialog } from '@watershed/ui-core/components/Dialog';
import Link from '@watershed/ui-core/components/TextLink';

import Callout from './Callout';
import KeyValueRows, { IRenderValue } from './KeyValueRows';

const valueSx: SxProps<Theme> = {
  whiteSpace: 'pre-wrap',
  margin: 0,
};

const messageSx: SxProps<Theme> = {
  color: (theme) => theme.palette.grey100,
  margin: 0,
  whiteSpace: 'pre-wrap',
  fontWeight: 'normal',
};

const messageSingleLineSx: SxProps<Theme> = {
  font: 'inherit',
  fontWeight: 'inherit',
};

const codeSx: SxProps<Theme> = {
  color: (theme) => theme.palette.grey100,
  fontFamily: (theme) => theme.typography.fontFamilyMonospace,
  fontWeight: 'normal',
  fontSize: 12,
  wordBreak: 'break-all',
};

const detailsButtonSx: SxProps<Theme> = {
  color: 'inherit',
  font: 'inherit',
  verticalAlign: 'baseline',
  textDecoration: 'underline',
  fontWeight: (theme) => theme.typography.fontWeightBold,
};

const wrapperSx: SxProps<Theme> = {
  margin: -2,
};

/**
 * Renders a detail value on the left and a new-tab link to related information
 * on the right.
 */
function DetailValueWithRelatedLink({
  renderedValue,
  href,
  linkText,
}: {
  renderedValue: React.ReactNode;
  href: string;
  linkText: string;
}) {
  return (
    <Box display="flex" alignItems="center">
      <Box flexGrow={1}>{renderedValue}</Box>
      <Box flexShrink={0}>
        <Link target="_blank" href={href}>
          <Box display="flex" alignItems="center">
            <PopOutIcon style={{ display: 'block' }} size="1rem" />
            <div>{linkText}</div>
          </Box>
        </Link>
      </Box>
    </Box>
  );
}

function DetailValue({
  detailKey,
  detailValue,
  detailData,
}: {
  detailKey: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  detailValue: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  detailData: Map<string, any>;
}) {
  const stringifiedDetailValue =
    typeof detailValue === 'object'
      ? JSON.stringify(detailValue, null, 2)
      : String(detailValue).trim();

  const renderedValue = (
    <Box component="pre" sx={mixinSx(valueSx, codeSx)}>
      <Linkify textDecorator={(urlText) => urlText.split(/\?/)[0]}>
        {stringifiedDetailValue}
      </Linkify>
    </Box>
  );

  if (detailKey === 'runId') {
    const workflowId = detailData.get('workflowId');
    const runId = detailData.get('runId');

    return (
      <DetailValueWithRelatedLink
        renderedValue={renderedValue}
        href={temporalWorkflowUrl(workflowId, runId)}
        linkText="View workflow history"
      />
    );
  }

  return renderedValue;
}

/**
 * It's useful to sort error properties so we can put the most meaningful ones
 * at the top of the list, the most verbose or least meaningful at the bottom.
 */
export function sortErrorProperties(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties: Array<[string, any]>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Array<[string, any]> {
  // Add to these lists when we have other known properties we want to control
  // sorting for. Any properties not in these lists will be in the middle of the
  // list in their original order.
  const firsts = [
    'message',
    'code',
    'errorType',
    'workflowType',
    'activityType',
    'workflowId',
    'runId',
    'activityId',
  ];
  const lasts = ['stackTrace'];

  return properties.slice().sort(([keyA], [keyB]) => {
    if (firsts.includes(keyA) && firsts.includes(keyB)) {
      return firsts.indexOf(keyA) - firsts.indexOf(keyB);
    }
    if (firsts.includes(keyA)) {
      return -1;
    }
    if (firsts.includes(keyB)) {
      return 1;
    }

    if (lasts.includes(keyA) && lasts.includes(keyB)) {
      return lasts.indexOf(keyA) - lasts.indexOf(keyB);
    }
    if (lasts.includes(keyA)) {
      return 1;
    }
    if (lasts.includes(keyB)) {
      return -1;
    }

    return 0;
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderValue: IRenderValue = (k: string, v: any, d: Map<string, any>) => {
  return <DetailValue detailKey={k} detailValue={v} detailData={d} />;
};

interface DetailedErrorProps {
  /**
   * Typically, `error` should be an Error instance, a string, or a
   * SerializedError object. But we'll try to coerce any value into a
   * presentable shape.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: Error | string | any;
  detailsButtonPosition?: 'right' | 'bottom';
}

/**
 * Render errors with an arbitrary number of possibly verbose details. Presents
 * just the `message` and `code` upfront, with the rest of the details stashed
 * in a dialog. Expected properties in the error's "details" might be rendered
 * in specialized ways, like a workflow run ID linking out to the Temporal UI.
 */
export default function DetailedError({
  error,
  detailsButtonPosition = 'right',
}: DetailedErrorProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  const normalizedError =
    error instanceof Error || typeof error === 'string'
      ? makeSerializableError(error)
      : error;
  const properties: Record<string, unknown> = {
    ...normalizedError.details,
    message: normalizedError.message,
    code: normalizedError.code,
    stackTrace: normalizedError.stackTrace,
    errorType: normalizedError.errorType,
  };
  delete properties.__typename;
  const hasDetails = Object.values(properties).filter(
    (x) => x != null && x !== ''
  ).length;
  const keyValueList = new Map(sortErrorProperties(Object.entries(properties)));

  const dialog = isDialogOpen && (
    <Dialog
      onClose={closeDialog}
      header={{ title: 'Error details' }}
      maxWidth="md"
      // Force the dialog to be on top of the snackbar.
      sx={{ zIndex: 9999 }}
      actions={[
        <Button key="ok" onClick={closeDialog} color="primary" type="button">
          <Trans context="Button copy">OK</Trans>
        </Button>,
      ]}
    >
      <Box sx={wrapperSx}>
        <KeyValueRows
          keyWidth={160}
          data={keyValueList}
          renderValue={renderValue}
          paddingX={16}
        />
      </Box>
    </Dialog>
  );

  const trimmedMessage = normalizedError.message.trim();
  const allMessageSx = mixinSx(
    messageSx,
    trimmedMessage.includes('\n') ? codeSx : messageSingleLineSx,
    { overflowWrap: 'anywhere' }
  );

  return (
    <>
      <Box
        display={detailsButtonPosition === 'right' ? 'flex' : 'block'}
        alignItems="baseline"
      >
        <Box flexGrow={1}>
          <Box component="pre" sx={allMessageSx}>
            {trimmedMessage}
          </Box>
          {normalizedError.code && (
            <Box component="code" sx={codeSx}>
              <Trans context="Error detail UI">Error code:</Trans>{' '}
              {normalizedError.code}
            </Box>
          )}
          {normalizedError.details?.message && (
            <>
              <br />
              <Box component="code" sx={codeSx}>
                <Trans context="Error details UI">Details:</Trans>{' '}
                {normalizedError.details.message}
              </Box>
            </>
          )}
        </Box>
        {hasDetails > 1 && (
          <Box
            flexShrink={0}
            marginLeft={detailsButtonPosition === 'right' ? 1 : 0}
            marginTop={detailsButtonPosition === 'bottom' ? 1 : 0}
          >
            <Link type="button" onClick={openDialog} sx={detailsButtonSx}>
              <Trans context="Link copy to view more details">Details</Trans>
            </Link>
          </Box>
        )}
      </Box>
      {dialog}
    </>
  );
}

/**
 * A DetailedError pre-wrapped in an ErrorBox.
 */
export function DetailedErrorBox({
  level = 'error',
  sx,
  ...props
}: DetailedErrorProps & {
  level?: 'error' | 'warning';
  sx?: SxProps<Theme>;
}) {
  return (
    <Callout
      variant={level}
      description={<DetailedError {...props} />}
      IconComponent={level === 'warning' ? WarningIcon : ErrorIcon}
      sx={sx}
    />
  );
}
